import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, photoUrl, formatApiError } from "@/lib/api";
import MapView from "@/components/MapView";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Camera, MapPin, X, Loader2, Locate } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Furniture", "Electronics", "Books/Media", "Kids' Stuff",
                    "Garden/Outdoor", "Clothes", "Kitchen", "Other"];

export default function PostItem() {
  const nav = useNavigate();
  const { user } = useAuth();
  const hideAddress = !!user?.preferences?.hide_exact_address;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [address, setAddress] = useState("");
  const [marker, setMarker] = useState(null);
  const [center, setCenter] = useState([40.7128, -74.006]);
  const [photos, setPhotos] = useState([]); // [{path, previewUrl}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        const c = [pos.coords.latitude, pos.coords.longitude];
        setCenter(c); setMarker(c);
      },
      () => {},
      { timeout: 5000 }
    );
    // Mount-only geolocation request; setCenter/setMarker are stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickFiles = async (files) => {
    const list = Array.from(files).slice(0, 5 - photos.length);
    setUploading(true); setError("");
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/items/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [...p, { path: data.path }]);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        const c = [pos.coords.latitude, pos.coords.longitude];
        setCenter(c); setMarker(c);
        toast.success("Pin set to your location");
      },
      () => toast.error("Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 6000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!marker) { setError("Please drop a pin on the map to mark the location."); return; }
    if (!title.trim()) { setError("Please add a title."); return; }
    setSubmitting(true);
    try {
      // Privacy preference: if "Hide exact address" is on, keep only the last
      // comma-separated chunk (typically the cross-street / neighbourhood),
      // stripping any house number from the start.
      let finalAddress = address.trim();
      if (hideAddress && finalAddress) {
        const parts = finalAddress.split(",").map((s) => s.trim()).filter(Boolean);
        finalAddress = parts[parts.length - 1].replace(/^\s*\d+\s*/, "");
      }
      const body = {
        title, description, category, address: finalAddress,
        lat: marker[0], lng: marker[1],
        photos: photos.map((p) => p.path),
      };
      const { data } = await api.post("/items", body);
      toast.success("Posted! Neighbours can see it now.");
      nav(`/items/${data.id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Post a curb item</h1>
        <p className="text-[#6B7280] mt-2">Snap up to 5 photos, drop a pin, and your neighbours will see it instantly.</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: details */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white rounded-3xl border border-[#E8E5F0] p-6 sm:p-8">
              <h2 className="font-display text-xl font-medium mb-5">Photos</h2>

              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos.map((p, i) => (
                  <div key={p.path} className="relative aspect-square rounded-xl overflow-hidden border border-[#E8E5F0] group">
                    <img src={photoUrl(p.path)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                      className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <label className="aspect-square rounded-xl border-2 border-dashed border-[#E8E5F0] grid place-items-center cursor-pointer hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors"
                    data-testid="add-photo">
                    {uploading
                      ? <Loader2 className="h-5 w-5 animate-spin text-[#7C3AED]" />
                      : <Camera className="h-5 w-5 text-[#6B7280]" />}
                    <input type="file" accept="image/*" multiple className="hidden"
                           onChange={(e) => onPickFiles(e.target.files)} />
                  </label>
                )}
              </div>
              <p className="text-xs text-[#6B7280] mt-2">JPG/PNG/WebP, up to 5 photos, 10MB each.</p>
            </div>

            <div className="bg-white rounded-3xl border border-[#E8E5F0] p-6 sm:p-8 space-y-4">
              <h2 className="font-display text-xl font-medium">Details</h2>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required
                  placeholder="e.g. Solid oak coffee table — free!"
                  className="mt-1.5 h-11 rounded-xl" data-testid="post-title" />
              </div>
              <div>
                <Label htmlFor="desc">Description</Label>
                <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Any details, condition, when it'll be out…"
                  className="mt-1.5 min-h-[100px] rounded-xl" data-testid="post-description" />
              </div>
              <div>
                <Label htmlFor="cat">Category</Label>
                <select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="post-category"
                  className="mt-1.5 w-full h-11 rounded-xl border border-input bg-white px-3 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="addr">Address / cross street (optional)</Label>
                <Input id="addr" value={address} onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. corner of Elm & Main"
                  className="mt-1.5 h-11 rounded-xl" data-testid="post-address" />
              </div>
            </div>
          </div>

          {/* Right: map */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl border border-[#E8E5F0] p-6 sm:p-8 lg:sticky lg:top-20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-medium">Drop a pin</h2>
                <button type="button" onClick={useMyLocation}
                  className="text-sm text-[#7C3AED] inline-flex items-center gap-1 hover:underline"
                  data-testid="use-my-location">
                  <Locate className="h-3.5 w-3.5" /> Use my location
                </button>
              </div>
              <p className="text-xs text-[#6B7280] mb-3">
                <MapPin className="inline h-3 w-3 mr-1 -mt-0.5" />
                Click on the map to place the pin where the item is.
              </p>
              <MapView center={center} zoom={14} pickable marker={marker}
                onPick={(c) => setMarker(c)} className="h-[300px] lg:h-[360px]" />
              {marker && (
                <p className="text-xs text-[#6B7280] mt-2" data-testid="pin-coords">
                  Pin: {marker[0].toFixed(5)}, {marker[1].toFixed(5)}
                </p>
              )}
              {error && <p className="text-sm text-red-600 mt-3" data-testid="post-error">{error}</p>}
              <Button type="submit" disabled={submitting}
                className="w-full mt-5 h-12 rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white text-base"
                data-testid="post-submit">
                {submitting ? "Posting…" : "Post to the curb"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
